/**
 * What Boone knows — the memory screen's reads and writes (issue #302).
 *
 * **Three endpoints and no fourth.** `GET /memory` is #299's paged read; `POST /remember` and
 * `POST /forget-this` are #298's routes onto the tools themselves. There is no panel-shaped
 * write here and there is not meant to be: a deletion from this screen *is* `forget_this` —
 * the same plain function, the same tier, the same `PROPOSAL#`, the same `ACTION#` and the
 * same #114 scrub — which is the whole of ADR 0021 decision 5.
 *
 * **The response types are derived from the server's routes, not declared here** — ADR 0025
 * decision 3. They were declared here on ADR 0021 decision 6's reasoning, that a shared
 * contract package waits until mobile is a real second caller. Mobile is now that caller, and
 * the answer turned out to be that the package carries the *request* and the server's own
 * derived types carry the response — a hand-written copy is a second statement of the contract
 * that is right until the day it silently is not.
 */

import type { FamilyPayloads } from "@pilotfish/api/src/app/contract.ts";
import {
  type AppApi,
  appFetch,
  confirmWrite,
  familyPath,
  newIdempotencyKey,
  postWrite,
  type WriteEnvelope,
} from "@pilotfish/app-api";

/**
 * Re-exported so the memory screen's imports stay one module deep (issue #310 moved the
 * bodies out).
 *
 * These three are the plumbing every panel write shares — see `writes.ts`, which exists
 * because this file and `roster.ts` each carried an identical copy and #310 was about to
 * write a third.
 */
export { confirmWrite, newIdempotencyKey, type WriteEnvelope };

/** One durable fact, as `disclosure.ts` shows it to the family it belongs to. */
/**
 * **What Boone has been asked to remember, as the server answers it** —
 * `FamilyPayloads["GET /memory"]`, derived from `reads.ts` rather than described here
 * (ADR 0025 decisions 3 and 4).
 *
 * Everything the family has asked Boone to remember, *including what retrieval hides*: this
 * read drops the status filter deliberately, so an archived fact arrives beside a live one and
 * the screen must render the difference. Showing an archived fact as live is precisely the
 * "presents uncertain memories as facts" failure.
 */
export type MemoryPage = FamilyPayloads["GET /memory"];

/**
 * One durable fact.
 *
 * `rawText` — the family's own words — is disclosed to this surface and to no model-facing
 * one: ADR 0012 decision 6's rule is scoped to a *host*, and `disclosure.ts` states the
 * exception for the family themselves.
 */
export type MemoryFact = MemoryPage["records"][number];

/**
 * **What a `remember` answers with** — `FamilyPayloads["POST /remember"]`, which is
 * `remember`'s own output type.
 */
export type RememberEnvelope = FamilyPayloads["POST /remember"];

/** The fact a colliding `remember` would overwrite — shown before the family answers (#92). */
export type CollisionSummary = NonNullable<RememberEnvelope["collision"]>;

/**
 * **What a `forget_this` answers with** — `FamilyPayloads["POST /forget-this"]`, which is
 * `forget_this`'s own output type.
 */
export type ForgetEnvelope = FamilyPayloads["POST /forget-this"];

/** What correcting a saved fact answers with. */
export type CorrectionEnvelope = FamilyPayloads["POST /correct-this"];

/** What `forget_this` is about to take, in the family's terms (#114, #241). */
export type ForgetTarget = NonNullable<ForgetEnvelope["deleting"]>;

export type FactSubject = { kind: "family" } | { kind: "member"; memberId: string };

/**
 * One page of the record.
 *
 * **`limit` is stated rather than defaulted** (PR #336 review, finding 5, which asked for this
 * across both walking screens rather than leaving them to diverge). An absent one falls back to
 * `DEFAULT_PAGE_SIZE` — fifty — so reading the whole record took four times the round trips it
 * needed. `pageSize` clamps at `MAX_PAGE_SIZE`, and a short page is not a lost record: the
 * cursor is followed either way.
 */
export function fetchMemoryPage(
  api: AppApi,
  familyId: string,
  after: string | null,
  limit: number,
): Promise<MemoryPage> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (after !== null) query.set("after", after);
  return appFetch<MemoryPage>(api, familyPath(familyId, `/memory?${query.toString()}`));
}

/** What the screen sends to save a fact — new, or a correction to one that exists. */
export interface RememberBody {
  readonly contextType: string;
  readonly title: string;
  readonly content: string;
  readonly subject: FactSubject;
  /**
   * The words the person typed.
   *
   * This is what makes `provenance: "directed"` honestly theirs rather than something the
   * panel manufactured (ADR 0014 decision 4): they typed it, into a field under a heading they
   * chose, and it lands on a record they can read back.
   */
  readonly rawText: string;
  readonly idempotencyKey: string;
}

export function proposeRemember(
  api: AppApi,
  familyId: string,
  body: RememberBody,
): Promise<RememberEnvelope> {
  return postWrite<RememberEnvelope>(api, familyId, "/remember", {
    ...body,
    provenance: "directed",
  });
}

/**
 * Correct one fact (issues #83, #306) — **one write, whatever the family changed**.
 *
 * `remember` upserts on a title-derived key, so sending a corrected *title* through it creates
 * a second fact and leaves the first live. `correct_this` is addressed by the fact instead, so
 * a changed name moves the row and a changed sentence rewrites it, in one transaction and
 * behind one confirmation — which is what lets the Edit form be one form.
 *
 * Only what changed is sent. Absent means unchanged, so the panel cannot blank a field by
 * failing to mention it — which is the trap `remember` set for #302 and `carriedOver` had to
 * work around.
 *
 * No `provenance`, unlike {@link proposeRemember}: the tool has no such field, which is what
 * makes it always propose (ADR 0014 decision 4's structural floor).
 */
export function proposeCorrection(
  api: AppApi,
  familyId: string,
  body: {
    contextType: string;
    title: string;
    newTitle?: string;
    content?: string;
    subject?: FactSubject;
    idempotencyKey: string;
  },
): Promise<CorrectionEnvelope> {
  return postWrite<CorrectionEnvelope>(api, familyId, "/correct-this", { ...body });
}

export function proposeForget(
  api: AppApi,
  familyId: string,
  body: { contextType: string; title: string; idempotencyKey: string },
): Promise<ForgetEnvelope> {
  return postWrite<ForgetEnvelope>(api, familyId, "/forget-this", { ...body });
}
